/**
 * Permission Helper Utility
 * Centralizes permission checking logic for UI components
 */

// Get user permissions from Redux store or localStorage
export const getUserPermissions = (user) => {
  if (!user || !user.permissions) return [];
  return user.permissions;
};

// Check if user has a specific permission
export const hasPermission = (user, permission) => {
  if (!user || !user.permissions) return false;
  
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  
  // Super Admin has all permissions
  if (permissions.includes('SuperAdmin.All.Access')) return true;
  
  // Check specific permission
  return permissions.includes(permission);
};

// Check if user has any of the provided permissions (OR logic)
export const hasAnyPermission = (user, permissionsList) => {
  if (!user || !user.permissions) return false;
  
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  
  // Super Admin has all permissions
  if (permissions.includes('SuperAdmin.All.Access')) return true;
  
  // Check if user has any of the permissions
  return permissionsList.some(permission => permissions.includes(permission));
};

// Check if user has all of the provided permissions (AND logic)
export const hasAllPermissions = (user, permissionsList) => {
  if (!user || !user.permissions) return false;
  
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  
  // Super Admin has all permissions
  if (permissions.includes('SuperAdmin.All.Access')) return true;
  
  // Check if user has all of the permissions
  return permissionsList.every(permission => permissions.includes(permission));
};

// Check if user is Super Admin
export const isSuperAdmin = (user) => {
  if (!user || !user.permissions) return false;
  
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  return permissions.includes('SuperAdmin.All.Access') || user.role === 'Super Admin';
};

// CRUD Permission Checkers
export const canCreate = (user, resource) => {
  return hasPermission(user, `${resource}.Create`);
};

export const canRead = (user, resource) => {
  return hasPermission(user, `${resource}.Read`);
};

export const canUpdate = (user, resource) => {
  return hasPermission(user, `${resource}.Update`);
};

export const canDelete = (user, resource) => {
  return hasPermission(user, `${resource}.Delete`) || isSuperAdmin(user);
};

// Specific Resource Permission Checkers
export const canManageUsers = (user) => {
  return hasAnyPermission(user, [
    'Users.Create', 
    'Users.Update', 
    'Users.Delete', 
    'SuperAdmin.Users.Create'
  ]);
};

export const canManageRoles = (user) => {
  return hasAnyPermission(user, [
    'Roles.Create', 
    'Roles.Update', 
    'Roles.Delete', 
    'SuperAdmin.Roles.Manage'
  ]);
};

export const canManageSuppliers = (user) => {
  return hasAnyPermission(user, [
    'Suppliers.Create', 
    'Suppliers.Update', 
    'Suppliers.Delete'
  ]);
};

export const canManagePatients = (user) => {
  return hasAnyPermission(user, [
    'Patients.Create', 
    'Patients.Update', 
    'Patients.Delete'
  ]);
};

export const canAccessSettings = (user) => {
  return hasAnyPermission(user, [
    'Settings', 
    'System.Settings.Manage'
  ]);
};

export const canAccessReports = (user) => {
  return hasAnyPermission(user, [
    'Reports', 
    'Reports.Financial.Access', 
    'Reports.Audit.Access'
  ]);
};

// Module Access Checkers
export const canAccessModule = (user, module) => {
  return hasPermission(user, module);
};

// Show/Hide UI Elements based on permissions
export const showDeleteButton = (user, resource) => {
  return canDelete(user, resource);
};

export const showCreateButton = (user, resource) => {
  return canCreate(user, resource);
};

export const showUpdateButton = (user, resource) => {
  return canUpdate(user, resource);
};

// Filter data based on permissions
export const filterByPermissions = (user, items, permission) => {
  if (isSuperAdmin(user)) return items;
  
  if (!hasPermission(user, permission)) return [];
  
  return items;
};

// Get visible menu items based on permissions
export const getVisibleMenuItems = (user, menuItems) => {
  if (isSuperAdmin(user)) return menuItems;
  
  return menuItems.filter(item => {
    if (!item.permission) return true;
    return hasPermission(user, item.permission);
  });
};

// Permission-based routing guard
export const canAccessRoute = (user, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  
  if (isSuperAdmin(user)) return true;
  
  return hasAnyPermission(user, requiredPermissions);
};

// Get permission level for a resource
export const getResourcePermissionLevel = (user, resource) => {
  const permissions = {
    create: canCreate(user, resource),
    read: canRead(user, resource),
    update: canUpdate(user, resource),
    delete: canDelete(user, resource),
  };
  
  return permissions;
};

// Permission constants for easy reference
export const PERMISSIONS = {
  // Module Access
  DASHBOARD: 'Dashboard',
  CONSULTATION: 'Consultation',
  PATIENTS: 'Patients',
  APPOINTMENTS: 'Appointments',
  CONSULTATIONS: 'Consultations',
  PATHOLOGY: 'Pathology',
  RADIOLOGY: 'Radiology',
  NURSING: 'Nursing',
  PHARMACY: 'Pharmacy',
  BILLING: 'Billing & Payments',
  REPORTS: 'Reports',
  USER: 'User',
  SETTINGS: 'Settings',
  ROLE: 'Role',
  MASTER: 'Master',
  
  // CRUD Operations
  PATIENTS_CREATE: 'Patients.Create',
  PATIENTS_READ: 'Patients.Read',
  PATIENTS_UPDATE: 'Patients.Update',
  PATIENTS_DELETE: 'Patients.Delete',
  
  APPOINTMENTS_CREATE: 'Appointments.Create',
  APPOINTMENTS_READ: 'Appointments.Read',
  APPOINTMENTS_UPDATE: 'Appointments.Update',
  APPOINTMENTS_DELETE: 'Appointments.Delete',
  
  CONSULTATIONS_CREATE: 'Consultations.Create',
  CONSULTATIONS_READ: 'Consultations.Read',
  CONSULTATIONS_UPDATE: 'Consultations.Update',
  CONSULTATIONS_DELETE: 'Consultations.Delete',
  
  PATHOLOGY_CREATE: 'Pathology.Create',
  PATHOLOGY_READ: 'Pathology.Read',
  PATHOLOGY_UPDATE: 'Pathology.Update',
  PATHOLOGY_DELETE: 'Pathology.Delete',
  
  RADIOLOGY_CREATE: 'Radiology.Create',
  RADIOLOGY_READ: 'Radiology.Read',
  RADIOLOGY_UPDATE: 'Radiology.Update',
  RADIOLOGY_DELETE: 'Radiology.Delete',
  
  USERS_CREATE: 'Users.Create',
  USERS_READ: 'Users.Read',
  USERS_UPDATE: 'Users.Update',
  USERS_DELETE: 'Users.Delete',
  
  SUPPLIERS_CREATE: 'Suppliers.Create',
  SUPPLIERS_READ: 'Suppliers.Read',
  SUPPLIERS_UPDATE: 'Suppliers.Update',
  SUPPLIERS_DELETE: 'Suppliers.Delete',
  
  ROLES_CREATE: 'Roles.Create',
  ROLES_READ: 'Roles.Read',
  ROLES_UPDATE: 'Roles.Update',
  ROLES_DELETE: 'Roles.Delete',
  
  // Super Admin
  SUPER_ADMIN_ALL: 'SuperAdmin.All.Access',
  SUPER_ADMIN_USERS: 'SuperAdmin.Users.Create',
  SUPER_ADMIN_ROLES: 'SuperAdmin.Roles.Manage',
  SUPER_ADMIN_SYSTEM: 'SuperAdmin.System.Configure',
};

export default {
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isSuperAdmin,
  canCreate,
  canRead,
  canUpdate,
  canDelete,
  canManageUsers,
  canManageRoles,
  canManageSuppliers,
  canManagePatients,
  canAccessSettings,
  canAccessReports,
  canAccessModule,
  showDeleteButton,
  showCreateButton,
  showUpdateButton,
  filterByPermissions,
  getVisibleMenuItems,
  canAccessRoute,
  getResourcePermissionLevel,
  PERMISSIONS
}; 